import { useForm, UseFormReturn } from 'react-hook-form';
import type { DefaultValues } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  UserProfileTypes,
  type IndependentIraUserCheckPayload,
  type IndependentIraUserRequestForm,
} from 'services/requests';
import { useQueryParams } from 'hooks/routes/useQueryParams';
import { useSubscribeUpdate } from 'hooks/common/useSubscribeUpdate';
import {
  yupLastName,
  yupMiddleName,
  yupEmail,
  yupFirstName,
  yupAnnualFee,
  yupEtzFee,
  yupCustodyFeePercentage,
  yupCustodyFeeAbsolute,
  yupNextBillingDate,
  yupCustodian,
  yupBusinessName,
} from 'utils/yupSchemaFields';
import { QueryKeys } from 'constants/queryKeys';
import { Parameters } from 'constants/queryParameters';
import { USER_STATUS } from 'constants/status';
import { IraAccountsErrors } from './formErrors';

export enum IraAccountFieldNames {
  email = 'email',
  firstName = 'firstName',
  middleName = 'middleName',
  lastName = 'lastName',
  profileType = 'profileType',
  custodianType = 'custodianType',
  businessName = 'businessName',
  annualFee = 'annualFee',
  etzFee = 'etzFee',
  custodyFeePercentage = 'custodyFeePercentage',
  custodyFeeAbsolute = 'custodyFeeAbsolute',
  nextBillingDate = 'nextBillingDate',
}

export const useIraAccountForm = (
  formPayload?: IndependentIraUserCheckPayload | null,
): UseFormReturn<IndependentIraUserRequestForm> => {
  const schema: yup.SchemaOf<IndependentIraUserRequestForm> = yup.object({
    email: yupEmail,
    firstName: yupFirstName,
    middleName: yupMiddleName,
    lastName: yupLastName,
    profileType: yup
      .string()
      .required(IraAccountsErrors.type)
      .oneOf(
        [
          UserProfileTypes.traditional,
          UserProfileTypes.roth,
          UserProfileTypes.inheritedTraditional,
          UserProfileTypes.inheritedRoth,
          UserProfileTypes.hsa,
          UserProfileTypes.esa,
          UserProfileTypes.sepFreelancer,
          UserProfileTypes.simpleIra,
          UserProfileTypes.solo401kFreelancer,
          UserProfileTypes.rothSolo401k,
          UserProfileTypes.rothSepIra,
          UserProfileTypes.rothSimpleIra,
          UserProfileTypes.corporate401kSubAccount,
        ],
        IraAccountsErrors.type,
      ),
    custodianType: yupCustodian,
    businessName: yupBusinessName,
    annualFee: yupAnnualFee,
    etzFee: yupEtzFee,
    custodyFeePercentage: yupCustodyFeePercentage,
    custodyFeeAbsolute: yupCustodyFeeAbsolute,
    nextBillingDate: yupNextBillingDate,
  });
  const queryClient = useQueryClient();
  const id = useQueryParams(Parameters.independentIraUserId);
  const queryKey = [QueryKeys.independentIraUser, id];
  const defaultValues: DefaultValues<IndependentIraUserRequestForm> =
    queryClient.getQueryState<IndependentIraUserRequestForm>(queryKey)?.data ?? {
      email: formPayload?.email,
      firstName: formPayload?.firstName,
      lastName: formPayload?.lastName,
      middleName: formPayload?.middleName,
      profileType: formPayload?.profileType,
      custodianType: formPayload?.custodianType,
      businessName: formPayload?.businessName,
      etzFee: formPayload?.etzFee,
      nextBillingDate: formPayload?.nextBillingDate,
      annualFee:
        formPayload?.annualFee !== undefined ? String(formPayload.annualFee) : undefined,
      custodyFeePercentage:
        formPayload?.custodyFeePercentage !== undefined
          ? String(formPayload.custodyFeePercentage)
          : undefined,
      custodyFeeAbsolute:
        formPayload?.custodyFeeAbsolute !== undefined
          ? String(formPayload.custodyFeeAbsolute)
          : undefined,
    };

  const form = useForm<IndependentIraUserRequestForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: yupResolver(schema),
    defaultValues,
    context: { isActive: formPayload?.profileStatus === USER_STATUS.active },
  });

  useSubscribeUpdate(queryKey, (data?: IndependentIraUserRequestForm) => {
    if (data) {
      form.reset({
        ...data,
        nextBillingDate: data.nextBillingDate ? data.nextBillingDate : '',
        custodyFeeAbsolute: data.custodyFeeAbsolute
          ? String(data.custodyFeeAbsolute)
          : '',
        custodyFeePercentage: data.custodyFeePercentage
          ? String(data.custodyFeePercentage)
          : '',
      });
    }
  });

  return form;
};
